import { alpha } from "@mui/material"
import fp from "lodash/fp"

import type {
  PowerlinePlotDef,
  LinePlotView,
  SingleStatPlotView,
  TableStatPlotView,
  SpanHeatmapPlotView,
} from "types/dashboard.types"

import { darkTheme } from "styles/themes"
import { lineView, singleStatView } from "helpers/config/dashboards/base/views"
import {
  linePlotDefBase,
  singleStatPlotDefBase,
  tableStatPlotDefBase,
} from "helpers/config/dashboards/base/defs"
import { getFieldFormat } from "helpers/utils/telemetryFields"
import {
  AMPACITY_AAR_FIELD,
  AMPACITY_DLR_FIELD,
  AMPACITY_FORECAST_AAR_FIELD,
  AMPACITY_FORECAST_DLR_FIELD,
  AMPACITY_SHORT_TERM_05_FIELD,
  AMPACITY_SHORT_TERM_10_FIELD,
  AMPACITY_SHORT_TERM_15_FIELD,
  POWERLINE_CURRENT_FIELD,
} from "helpers/config/telemetryFieldDefs"

// ─── Ampacity ─────────────────────────────────────────────────────────────────

const LONG_TERM_AMPACITY_FIELDS = [
  AMPACITY_AAR_FIELD,
  AMPACITY_DLR_FIELD,
  AMPACITY_FORECAST_AAR_FIELD,
  AMPACITY_FORECAST_DLR_FIELD,
]

const longTermAmpacityPlotView: LinePlotView = fp.merge(lineView, {
  name: "Long-term ampacity",
  nameKey: "LONG_TERM_AMPACITY",
  helpTooltip: [
    "plots_descriptions.LONG_TERM_AMPACITY",
    "plots_descriptions.AMPACITY_AAR",
    "plots_descriptions.AMPACITY_DLR",
    "plots_descriptions.NOMINAL_AMPACITY_REFERENCE",
  ],
  properties: {
    axes: {
      y: getFieldFormat(LONG_TERM_AMPACITY_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${AMPACITY_AAR_FIELD}"}`]: { color: darkTheme.palette.primary.main },
      [`{"_field":"${AMPACITY_DLR_FIELD}"}`]: { color: darkTheme.palette.secondary.main },
      [`{"_field":"${AMPACITY_FORECAST_AAR_FIELD}"}`]: {
        color: darkTheme.palette.extras.lavender.main,
        backgroundShading: true,
      },
      [`{"_field":"${AMPACITY_FORECAST_DLR_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
        backgroundShading: true,
      },
    },
    lineFills: [
      {
        lineA: AMPACITY_FORECAST_DLR_FIELD,
        lineB: AMPACITY_FORECAST_AAR_FIELD,
        color: alpha(darkTheme.palette.extras.limeGreen.main, 0.15),
      },
    ],
  },
})

export const longTermAmpacityPlotDef: PowerlinePlotDef = fp.merge(linePlotDefBase, {
  view: longTermAmpacityPlotView,
  dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: LONG_TERM_AMPACITY_FIELDS,
    forecastFields: [AMPACITY_FORECAST_AAR_FIELD, AMPACITY_FORECAST_DLR_FIELD],
    aggregate: ["mean"],
    group: ["powerline_id", "_field"],
    measurement: "dlr",
  },
})

const lastAmpacityAARPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last ampacity (AAR)",
  nameKey: "LAST_AMPACITY_AAR",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([AMPACITY_AAR_FIELD]),
    size: "lg",
    formatUnits: true,
    decimalPlaces: 0,
  },
})

export const lastAmpacityAARPlotDef: PowerlinePlotDef = fp.merge(singleStatPlotDefBase, {
  view: lastAmpacityAARPlotView,
  dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [AMPACITY_AAR_FIELD],
    method: ["last"],
    group: ["powerline_id", "_field"],
    measurement: "dlr",
  },
})

const lastAmpacityDLRPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last ampacity (DLR)",
  nameKey: "LAST_AMPACITY_DLR",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([AMPACITY_DLR_FIELD]),
    size: "lg",
    formatUnits: true,
    decimalPlaces: 0,
  },
})

export const lastAmpacityDLRPlotDef: PowerlinePlotDef = fp.merge(singleStatPlotDefBase, {
  view: lastAmpacityDLRPlotView,
  dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [AMPACITY_DLR_FIELD],
    method: ["last"],
    group: ["powerline_id", "_field"],
    measurement: "dlr",
  },
})

// Mirrors longTermAmpacityPlotDef's reqConfig (aar/dlr, aggregate mean) so the percentage
// is computed over the same series the line plot shows. The nominal threshold is
// injected dynamically by useNominalAmpacityViews.

const belowNominalAmpacityTableView: TableStatPlotView = {
  name: "% Below nominal ampacity",
  nameKey: "BELOW_NOMINAL_AMPACITY",
  type: "table-stat",
  properties: {
    columns: [
      { key: "method", labelKey: "table_stats.METHOD", type: "label" },
      { key: "percentage", labelKey: "table_stats.PERCENTAGE", type: "percentageBar" },
    ],
    rows: [
      {
        field: AMPACITY_DLR_FIELD,
        labelKey: "table_stats.DLR_FULL",
        color: darkTheme.palette.secondary.main,
      },
      {
        field: AMPACITY_AAR_FIELD,
        labelKey: "table_stats.AAR_FULL",
        color: darkTheme.palette.primary.main,
      },
    ],
  },
}

export const belowNominalAmpacityTablePlotDef: PowerlinePlotDef = fp.merge(
  tableStatPlotDefBase,
  {
    view: belowNominalAmpacityTableView,
    dependencies: [longTermAmpacityPlotView.nameKey],
    downloadable: false,
    dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
    reqConfig: {
      fields: [AMPACITY_AAR_FIELD, AMPACITY_DLR_FIELD],
      aggregate: ["mean"],
      group: ["powerline_id", "_field"],
      measurement: "dlr",
      fetchQualitySummary: false,
    },
  },
)

// ─── Short-term ampacity ──────────────────────────────────────────────────────

const SHORT_TERM_AMPACITY_FIELDS = [
  AMPACITY_SHORT_TERM_05_FIELD,
  AMPACITY_SHORT_TERM_10_FIELD,
  AMPACITY_SHORT_TERM_15_FIELD,
]

const shortTermAmpacityPlotView: LinePlotView = fp.merge(lineView, {
  name: "Short-term ampacity",
  nameKey: "SHORT_TERM_AMPACITY",
  helpTooltip: ["plots_descriptions.SHORT_TERM_AMPACITY"],
  properties: {
    axes: {
      y: getFieldFormat(SHORT_TERM_AMPACITY_FIELDS),
    },
    fieldProperties: {
      [`{"_field":"${AMPACITY_SHORT_TERM_05_FIELD}"}`]: {
        color: darkTheme.palette.primary.main,
      },
      [`{"_field":"${AMPACITY_SHORT_TERM_10_FIELD}"}`]: {
        color: darkTheme.palette.secondary.main,
      },
      [`{"_field":"${AMPACITY_SHORT_TERM_15_FIELD}"}`]: {
        color: darkTheme.palette.extras.limeGreen.main,
      },
    },
  },
})

export const shortTermAmpacityPlotDef: PowerlinePlotDef = fp.merge(linePlotDefBase, {
  view: shortTermAmpacityPlotView,
  dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: SHORT_TERM_AMPACITY_FIELDS,
    aggregate: ["mean"],
    group: ["powerline_id", "_field"],
    measurement: "dlr",
  },
  tabsConfig: {
    tabs: [
      {
        id: "05",
        labelKey: "plot_legends.FIVE_MIN",
        fields: [AMPACITY_SHORT_TERM_05_FIELD],
      },
      {
        id: "10",
        labelKey: "plot_legends.TEN_MIN",
        fields: [AMPACITY_SHORT_TERM_10_FIELD],
      },
      {
        id: "15",
        labelKey: "plot_legends.FIFTEEN_MIN",
        fields: [AMPACITY_SHORT_TERM_15_FIELD],
      },
    ],
    defaultTab: "all",
    showAllTab: true,
  },
})

const activeShortTermAmpacityTableView: TableStatPlotView = {
  name: "Active short-term ampacity",
  nameKey: "ACTIVE_SHORT_TERM_AMPACITY",
  type: "table-stat",
  helpTooltip: ["plots_descriptions.SHORT_TERM_AMPACITY"],
  properties: {
    ...getFieldFormat(SHORT_TERM_AMPACITY_FIELDS),
    decimalPlaces: 0,
    showWhenEmpty: true,
    columns: [
      { key: "timeframe", labelKey: "table_stats.TIMEFRAME", type: "label" },
      { key: "ampacity", labelKey: "table_stats.AMPACITY", type: "value" },
      { key: "timestamp", labelKey: "table_stats.TIMESTAMP", type: "timestamp" },
    ],
    rows: [
      {
        field: AMPACITY_SHORT_TERM_05_FIELD,
        labelKey: "table_stats.NEXT_5_MIN",
        color: darkTheme.palette.primary.main,
      },
      {
        field: AMPACITY_SHORT_TERM_10_FIELD,
        labelKey: "table_stats.NEXT_10_MIN",
        color: darkTheme.palette.secondary.main,
      },
      {
        field: AMPACITY_SHORT_TERM_15_FIELD,
        labelKey: "table_stats.NEXT_15_MIN",
        color: darkTheme.palette.extras.limeGreen.main,
      },
    ],
  },
}

export const activeShortTermAmpacityTablePlotDef: PowerlinePlotDef = fp.merge(
  tableStatPlotDefBase,
  {
    view: activeShortTermAmpacityTableView,
    dependencies: [shortTermAmpacityPlotView.nameKey],
    dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
    reqConfig: {
      fields: [
        AMPACITY_SHORT_TERM_05_FIELD,
        AMPACITY_SHORT_TERM_10_FIELD,
        AMPACITY_SHORT_TERM_15_FIELD,
      ],
      method: ["last"],
      group: ["powerline_id", "_field"],
      measurement: "dlr",
    },
  },
)

// ─── Current ──────────────────────────────────────────────────────────────────

const powerlineCurrentPlotView: LinePlotView = fp.merge(lineView, {
  name: "Current",
  nameKey: "CURRENT",
  properties: {
    axes: {
      y: getFieldFormat([POWERLINE_CURRENT_FIELD]),
    },
    fieldProperties: {
      [POWERLINE_CURRENT_FIELD]: { color: "#009fe3" },
    },
  },
})

export const powerlineCurrentPlotDef: PowerlinePlotDef = fp.merge(linePlotDefBase, {
  view: powerlineCurrentPlotView,
  dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
  reqConfig: {
    fields: [POWERLINE_CURRENT_FIELD],
    aggregate: ["mean"],
    method: ["timedMovingAverage"],
    group: ["powerline_id", "_field"],
    measurement: "dlr",
  },
})

const lastPowerlineCurrentPlotView: SingleStatPlotView = fp.merge(singleStatView, {
  name: "Last current",
  nameKey: "LAST_CURRENT",
  properties: {
    footerFields: ["_time"],
    ...getFieldFormat([POWERLINE_CURRENT_FIELD]),
    size: "lg",
    decimalPlaces: 0,
  },
})

export const lastPowerlineCurrentPlotDef: PowerlinePlotDef = fp.merge(
  singleStatPlotDefBase,
  {
    view: lastPowerlineCurrentPlotView,
    dataSource: { entity: "powerline", resolver: "self", service: "telemetry" },
    reqConfig: {
      fields: [POWERLINE_CURRENT_FIELD],
      method: ["last"],
      group: ["powerline_id", "_field"],
      measurement: "dlr",
    },
  },
)

// The span map's id in the powerline grid. Owned here so the layout, the grid
// child and the card all name the same cell. Internal, never user-facing.
export const SPAN_LAYOUT_MAP = "SPAN_LAYOUT_MAP"

export const limitingSpanFrequencyView: SpanHeatmapPlotView = {
  name: "Limiting spans",
  // No `plot_titles.LIMITING_SPANS` behind it: this view never goes through
  // `Plot`, so nothing resolves the key. `SpanLayoutMap` renders the card's own
  // title and its grid cell is keyed by `SPAN_LAYOUT_MAP`.
  nameKey: "LIMITING_SPANS",
  helpTooltip: ["plots_descriptions.LIMITING_SPANS"],
  type: "span-heatmap",
  properties: {
    series: [
      {
        dataKey: "aar",
        labelKey: "powerlines.LIMITING_SPAN_AAR",
        field: "ampacity.aar.critical_span",
      },
      {
        dataKey: "dlr",
        labelKey: "powerlines.LIMITING_SPAN_DLR",
        field: "ampacity.dlr.critical_span",
      },
    ],
  },
}

/**
 * Measurement the limiting-span fields live in. `SpanLayoutMap` owns this
 * view rather than going through `Plot`, so the queries are issued by
 * `useLimitingSpanFrequency` and there is no plot def to hang a `reqConfig` off
 * — the fields themselves come from `limitingSpanFrequencyView.properties`.
 */
export const LIMITING_SPAN_MEASUREMENT = "dlr"
